import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import {
  onSessionExpired,
  getRefreshInProgress,
  setRefreshInProgress,
} from '../services/authBridge';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Impersonation state
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalAdmin, setOriginalAdmin] = useState(null);

  // Proactive token refresh
  const refreshTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Track user activity for proactive refresh gating
  useEffect(() => {
    const updateActivity = () => { lastActivityRef.current = Date.now(); };
    window.addEventListener('mousemove', updateActivity, { passive: true });
    window.addEventListener('keydown', updateActivity, { passive: true });
    window.addEventListener('click', updateActivity, { passive: true });
    return () => {
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('click', updateActivity);
    };
  }, []);

  const scheduleTokenRefresh = useCallback((expiresAt) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

    const timeUntilExpiry = expiresAt - Date.now();
    // Refresh at 80% of TTL (e.g., 12 min for 15-min token), minimum 30s
    const refreshAt = Math.max(timeUntilExpiry * 0.8, 30000);

    refreshTimerRef.current = setTimeout(async () => {
      // Only refresh if user was active in the last 30 minutes
      const inactiveDuration = Date.now() - lastActivityRef.current;
      if (inactiveDuration > 30 * 60 * 1000) return;

      if (getRefreshInProgress()) return;

      try {
        setRefreshInProgress(true);
        const response = await api.post('/auth/refresh');
        if (response.data.token_expires_at) {
          scheduleTokenRefresh(response.data.token_expires_at);
        }
      } catch {
        // Proactive refresh failed — the 401 interceptor will handle it on next API call
      } finally {
        setRefreshInProgress(false);
      }
    }, refreshAt);
  }, []);

  // Handle session-expired events from api.js interceptor
  useEffect(() => {
    const unsubscribe = onSessionExpired(() => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      setUser(null);
      sessionStorage.removeItem('impersonation');
      setIsImpersonating(false);
      setOriginalAdmin(null);
    });
    return unsubscribe;
  }, []);

  // Clean up refresh timer on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  const fetchUser = async ({ suppressAuthRedirect = false } = {}) => {
    try {
      const response = await api.get('/auth/me', { suppressAuthRedirect });
      setUser(response.data);
      setError(null);
      if (response.data.token_expires_at) {
        scheduleTokenRefresh(response.data.token_expires_at);
      }
    } catch (err) {
      console.error('Failed to fetch user:', err);
      sessionStorage.removeItem('impersonation');
      setUser(null);
      setIsImpersonating(false);
      setOriginalAdmin(null);
    } finally {
      setLoading(false);
    }
  };

  const checkImpersonationStatus = async () => {
    try {
      const response = await api.get('/auth/impersonation-status', { suppressAuthRedirect: true });
      if (response.data.isImpersonating) {
        setIsImpersonating(true);
        setOriginalAdmin(response.data.originalAdmin);
        sessionStorage.setItem('impersonation', JSON.stringify(response.data.originalAdmin));
      } else {
        setIsImpersonating(false);
        setOriginalAdmin(null);
        sessionStorage.removeItem('impersonation');
      }
    } catch (err) {
      console.error('Failed to check impersonation status:', err);
      setIsImpersonating(false);
      setOriginalAdmin(null);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        await api.get('/csrf-token').catch(() => {});
        await Promise.all([fetchUser({ suppressAuthRedirect: true }), checkImpersonationStatus()]);
      } catch {
        setLoading(false);
      }
    };
    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (username, password) => {
    try {
      setError(null);
      const response = await api.post('/auth/login', { username, password });
      const { user: userData, token_expires_at } = response.data;

      sessionStorage.removeItem('impersonation');
      setUser(userData);
      setIsImpersonating(false);
      setOriginalAdmin(null);

      if (token_expires_at) {
        scheduleTokenRefresh(token_expires_at);
      }

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      await api.post('/auth/logout');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      sessionStorage.removeItem('impersonation');
      setUser(null);
      setIsImpersonating(false);
      setOriginalAdmin(null);
    }
  };

  // Start impersonating a user (admin only)
  const startImpersonation = async (targetEmployeeId) => {
    try {
      setError(null);
      const response = await api.post(`/auth/impersonate/${targetEmployeeId}`);
      const { impersonatedUser, originalAdmin: admin } = response.data;

      sessionStorage.setItem('impersonation', JSON.stringify(admin));
      setUser(impersonatedUser);
      setIsImpersonating(true);
      setOriginalAdmin(admin);

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to start impersonation';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Stop impersonating and return to admin session
  const stopImpersonation = async () => {
    try {
      setError(null);
      const response = await api.post('/auth/stop-impersonation');
      const { user: adminUser } = response.data;

      sessionStorage.removeItem('impersonation');
      setUser(adminUser);
      setIsImpersonating(false);
      setOriginalAdmin(null);

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to stop impersonation';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Role checks - these check the current user's role (impersonated user if impersonating)
  const isAdmin = () => user?.role?.toLowerCase() === 'admin';
  const isCoordinator = () => ['coordinator', 'admin'].includes(user?.role?.toLowerCase());
  const isApprover = () => ['travel lead', 'admin'].includes(user?.role?.toLowerCase()) || user?.is_travel_lead;
  const isTraveler = () => !!user?.traveler_ID;
  const isDepartmentManager = () => !!user?.is_department_manager;

  // Check if the original (non-impersonated) user is an admin
  // This is useful for showing admin-only UI while impersonating
  const isActuallyAdmin = () => {
    if (isImpersonating && originalAdmin) {
      return true; // Only admins can impersonate, so if impersonating, original user is admin
    }
    return isAdmin();
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    isAdmin,
    isCoordinator,
    isApprover,
    isTraveler,
    isDepartmentManager,
    refreshUser: fetchUser,
    // Impersonation
    isImpersonating,
    originalAdmin,
    startImpersonation,
    stopImpersonation,
    isActuallyAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
